/**
 * Promo Products Modal — runtime
 * ──────────────────────────────
 * Generic, reusable modal that promo banners (min-qty, bundle, BXGY)
 * open to surface a grid of related products with Quick-Add.
 *
 * Public API on window.PromoProductsModal:
 *   open({eyebrow, title, sub, footnote, products, currentNumericId, mode}):
 *     opens the dialog, renders product cards. Re-callable while open
 *     to swap content (the modal cleans up between renders).
 *   close():  closes the dialog.
 *   isOpen(): boolean.
 *
 * Inputs:
 *   - eyebrow:           short uppercase tag ("Mix & match", "Bundle deal")
 *   - title:             headline ("Eligible products for 15% off")
 *   - sub:               supporting copy
 *   - footnote:          small print ("Discount applies automatically at checkout")
 *   - products:          ProductCard[] — see /api/storefront/active-promos
 *   - currentNumericId:  the PDP product (gets a "You're here" highlight,
 *                         loses Quick-Add since it's right there)
 *   - mode:              'min-qty' | 'bundle' (drives saving-pill rendering)
 *
 * Live cart-aware:
 *   - Listens to cart:updated to refresh in-cart badges without re-open.
 *   - Quick-Add POSTs to /cart/add.js then dispatches cart:updated so
 *     other modules (banner progress bars, drawer) re-render.
 *
 * Resilience:
 *   - /cart.js fetch retries with backoff (storefront throttle).
 *   - Quick-Add show inline error label on failure (no global toast —
 *     the card-local label keeps the user oriented).
 *   - Re-rendering is idempotent: clears the grid then rebuilds.
 */
(function () {
  'use strict';

  var SOURCE_LABEL = 'promo-products-modal';

  /* ──────────────────────────────────────────────────────
     Tiny utilities (intentionally inlined — see header of
     min-qty-promo.js for the rationale on duplicate utils).
  ────────────────────────────────────────────────────── */

  function fetchJsonResilient(url, init, retriesLeft) {
    if (typeof retriesLeft !== 'number') retriesLeft = 2;
    var backoffs = [1500, 3000];
    var attempt = 2 - retriesLeft;
    return fetch(url, init)
      .then(function (res) {
        if (res.status === 429 && retriesLeft > 0) {
          var wait = backoffs[Math.min(attempt, backoffs.length - 1)];
          return new Promise(function (r) { setTimeout(r, wait); })
            .then(function () { return fetchJsonResilient(url, init, retriesLeft - 1); });
        }
        if (!res.ok) throw new Error(url + ' → ' + res.status);
        return res.json();
      })
      .catch(function (err) {
        if (retriesLeft > 0) {
          var wait = backoffs[Math.min(attempt, backoffs.length - 1)];
          return new Promise(function (r) { setTimeout(r, wait); })
            .then(function () { return fetchJsonResilient(url, init, retriesLeft - 1); });
        }
        throw err;
      });
  }

  /** Format price string ("138.00") via Shopify.formatMoney with the
   *  shop's money format. Falls back to a passable "$X.XX" if Shopify
   *  isn't loaded (rare — it's part of the global theme). */
  function formatMoneyFromString(priceStr) {
    if (!priceStr) return '';
    var cents = Math.round(parseFloat(priceStr) * 100);
    if (!Number.isFinite(cents)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      var fmt = (window.brandCartDrawerConfig && window.brandCartDrawerConfig.moneyFormat)
        || (window.Shopify.money_format)
        || '${{amount}}';
      try { return window.Shopify.formatMoney(cents, fmt); } catch (e) { /* fall through */ }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ──────────────────────────────────────────────────────
     State
  ────────────────────────────────────────────────────── */

  var state = {
    open: false,
    products: [],          // current render's product list
    currentNumericId: null,
    mode: 'min-qty',
    cartLines: [],         // /cart.js items[]
    inflightCartFetch: null,
  };

  /* ──────────────────────────────────────────────────────
     DOM lookups (cached on first open — the snippet renders
     once, so the elements exist for the whole session)
  ────────────────────────────────────────────────────── */

  var dom = {
    dialog: null,
    eyebrow: null,
    title: null,
    sub: null,
    footnote: null,
    grid: null,
    empty: null,
    cardTemplate: null,
    closeBtn: null,
  };

  function cacheDom() {
    if (dom.dialog) return true;
    dom.dialog = document.querySelector('[data-promo-products-modal]');
    if (!dom.dialog) return false;
    dom.eyebrow = dom.dialog.querySelector('[data-promo-modal-eyebrow]');
    dom.title = dom.dialog.querySelector('[data-promo-modal-title]');
    dom.sub = dom.dialog.querySelector('[data-promo-modal-sub]');
    dom.footnote = dom.dialog.querySelector('[data-promo-modal-footnote]');
    dom.grid = dom.dialog.querySelector('[data-promo-modal-grid]');
    dom.empty = dom.dialog.querySelector('[data-promo-modal-empty]');
    dom.cardTemplate = dom.dialog.querySelector('[data-promo-modal-card-template]');
    dom.closeBtn = dom.dialog.querySelector('[data-promo-modal-close]');
    return true;
  }

  /* ──────────────────────────────────────────────────────
     Cart fetch + lookup
  ────────────────────────────────────────────────────── */

  function fetchCart() {
    if (state.inflightCartFetch) return state.inflightCartFetch;
    state.inflightCartFetch = fetchJsonResilient('/cart.js')
      .then(function (cart) {
        state.cartLines = (cart && cart.items) || [];
        state.inflightCartFetch = null;
        return cart;
      })
      .catch(function (err) {
        state.inflightCartFetch = null;
        console.warn('[' + SOURCE_LABEL + '] cart fetch failed:', err);
        return { items: state.cartLines };
      });
    return state.inflightCartFetch;
  }

  function inCartQtyForProduct(numericId) {
    var total = 0;
    var idStr = String(numericId);
    for (var i = 0; i < state.cartLines.length; i++) {
      if (String(state.cartLines[i].product_id) === idStr) {
        total += state.cartLines[i].quantity || 0;
      }
    }
    return total;
  }

  /* ──────────────────────────────────────────────────────
     Render
  ────────────────────────────────────────────────────── */

  function renderCard(product) {
    var clone = dom.cardTemplate.content.firstElementChild.cloneNode(true);
    var article = clone;
    article.dataset.numericId = product.numericId;
    if (product.variantId) {
      article.dataset.variantId = product.variantId;
    }

    var img = clone.querySelector('[data-card-image]');
    var media = clone.querySelector('[data-card-link]');
    var titleSpan = clone.querySelector('[data-card-title]');
    var titleLink = clone.querySelector('[data-card-title-link]');
    var price = clone.querySelector('[data-card-price]');
    var compare = clone.querySelector('[data-card-compare]');
    var inCartBadge = clone.querySelector('[data-card-incart]');
    var addBtn = clone.querySelector('[data-card-add]');
    var addLabel = clone.querySelector('[data-card-add-label]');
    var current = clone.querySelector('[data-card-current]');
    var saving = clone.querySelector('[data-card-saving]');

    var productUrl = '/products/' + product.handle;

    if (img) {
      if (product.image) {
        img.src = product.image;
        img.alt = product.title;
      } else {
        img.style.display = 'none';
      }
    }
    if (media) media.href = productUrl;
    if (titleLink) titleLink.href = productUrl;
    if (titleSpan) titleSpan.textContent = product.title;

    if (price) price.textContent = product.price ? 'WSP ' + formatMoneyFromString(product.price) : '';
    if (compare) {
      if (
        product.compareAtPrice &&
        product.price &&
        parseFloat(product.compareAtPrice) > parseFloat(product.price)
      ) {
        compare.textContent = formatMoneyFromString(product.compareAtPrice);
        compare.classList.remove('hidden');
      } else {
        compare.classList.add('hidden');
      }
    }

    var qtyInCart = inCartQtyForProduct(product.numericId);
    if (qtyInCart > 0 && inCartBadge) {
      inCartBadge.textContent = 'In cart: ' + qtyInCart;
      inCartBadge.classList.remove('hidden');
      article.classList.add('promo-products-card--in-cart');
    } else if (inCartBadge) {
      inCartBadge.classList.add('hidden');
    }

    var isCurrent =
      state.currentNumericId &&
      String(state.currentNumericId) === String(product.numericId);
    if (isCurrent && current) {
      current.classList.remove('hidden');
      article.classList.add('promo-products-card--current');
    }

    // Bundle mode — show "20% off bundle" pill on each card when not
    // current. Helpful visual nudge.
    if (state.mode === 'bundle' && saving && product.savingLabel && !isCurrent) {
      saving.textContent = product.savingLabel;
      saving.classList.remove('hidden');
    }

    if (addBtn && addLabel) {
      if (isCurrent) {
        // Current PDP product — Quick-Add doesn't make sense (the user
        // is already on its page; the PDP's own Add button is below).
        // Hide and let the title link be the primary action.
        addBtn.classList.add('promo-products-card__add--no-variant');
      } else if (!product.variantId) {
        // No variant resolved → Quick-Add unsafe; hide and let the
        // user click through to the PDP.
        addBtn.classList.add('promo-products-card__add--no-variant');
      } else {
        addLabel.textContent = qtyInCart > 0 ? 'Add another' : 'Add to cart';
        addBtn.dataset.variantId = product.variantId;
        addBtn.dataset.numericId = product.numericId;
        addBtn.dataset.handle = product.handle;
      }
    }

    return clone;
  }

  function renderGrid() {
    if (!dom.grid) return;
    dom.grid.innerHTML = '';
    if (!state.products.length) {
      if (dom.empty) dom.empty.classList.remove('hidden');
      return;
    }
    if (dom.empty) dom.empty.classList.add('hidden');
    var frag = document.createDocumentFragment();
    state.products.forEach(function (p) {
      frag.appendChild(renderCard(p));
    });
    dom.grid.appendChild(frag);
  }

  /* ──────────────────────────────────────────────────────
     Quick Add
  ────────────────────────────────────────────────────── */

  /** Extract numeric variant id from any of: "12345", "ProductVariant/12345",
   *  "gid://shopify/ProductVariant/12345". The server should emit numeric
   *  only (stripped in storefront-promos.ts) but BR Connect deploys lag
   *  occasionally — be defensive. */
  function variantIdToNumeric(raw) {
    if (raw == null) return null;
    var s = String(raw);
    var m = s.match(/(\d+)$/);
    return m ? parseInt(m[1], 10) : null;
  }

  function quickAdd(card) {
    var addBtn = card.querySelector('[data-card-add]');
    var rawVariantId = addBtn && addBtn.dataset.variantId;
    var variantNumeric = variantIdToNumeric(rawVariantId);
    if (!variantNumeric) return Promise.reject(new Error('no variantId'));
    var btn = addBtn;
    var label = card.querySelector('[data-card-add-label]');
    var origLabel = label ? label.textContent : 'Add to cart';
    // Pull the product title + vendor (from the matching state.products
    // entry) so we can dispatch cart:item-added the same shape the PDP
    // form does. brand-cart-drawer.js listens for that event and opens
    // the drawer for the matching vendor — without it the drawer never
    // shows up after a Quick Add and the user has no feedback that the
    // item is in the cart beyond the badge inside the modal.
    var numericId = card.dataset.numericId;
    var productMatch = state.products.find(function (p) {
      return String(p.numericId) === String(numericId);
    }) || {};
    var productTitle = productMatch.title || '';
    var vendor = productMatch.vendor || null;
    if (btn) btn.disabled = true;
    if (label) label.textContent = 'Adding…';
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantNumeric, quantity: 1 }] }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/add → ' + res.status);
        return res.json();
      })
      .then(function () {
        // Tell the world a cart change happened — promo banners refresh.
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { source: SOURCE_LABEL } }),
        );
        // Dispatch cart:item-added with vendor + productTitle so the
        // brand-cart-drawer auto-opens, mirroring the PDP form's
        // post-add behaviour. The drawer is the canonical confirmation
        // surface — leaving the user inside the modal alone breaks the
        // "you added it, here's where it is" feedback loop.
        if (vendor) {
          document.dispatchEvent(
            new CustomEvent('cart:item-added', {
              detail: { vendor: vendor, productTitle: productTitle, source: SOURCE_LABEL },
            }),
          );
          // Closing the modal lets the drawer slide in cleanly. If the
          // user wants to add more, they can re-open from the banner.
          // Defer the close so the success-state animation reads first.
          setTimeout(close, 300);
        }
        if (btn) {
          btn.classList.add('promo-products-card__add--success');
        }
        if (label) label.textContent = 'Added ✓';
        // Refresh in-cart badge for this card.
        return fetchCart().then(function () {
          var numericId = card.dataset.numericId;
          var qty = inCartQtyForProduct(numericId);
          var badge = card.querySelector('[data-card-incart]');
          if (badge && qty > 0) {
            badge.textContent = 'In cart: ' + qty;
            badge.classList.remove('hidden');
            card.classList.add('promo-products-card--in-cart');
          }
          // Reset button label after a beat, so user can add more.
          setTimeout(function () {
            if (btn) {
              btn.disabled = false;
              btn.classList.remove('promo-products-card__add--success');
            }
            if (label) label.textContent = qty > 0 ? 'Add another' : origLabel;
          }, 1400);
        });
      })
      .catch(function (err) {
        console.warn('[' + SOURCE_LABEL + '] add failed:', err);
        if (btn) btn.disabled = false;
        if (label) label.textContent = 'Try again';
        setTimeout(function () {
          if (label) label.textContent = origLabel;
        }, 2000);
      });
  }

  function bindGridDelegation() {
    if (!dom.grid || dom.grid.dataset.bound === '1') return;
    dom.grid.addEventListener('click', function (e) {
      var addBtn = e.target.closest && e.target.closest('[data-card-add]');
      if (!addBtn) return;
      e.preventDefault();
      var card = addBtn.closest('.promo-products-card');
      if (!card) return;
      quickAdd(card);
    });
    dom.grid.dataset.bound = '1';
  }

  /* ──────────────────────────────────────────────────────
     Reactive: cart:updated → refresh in-cart badges
  ────────────────────────────────────────────────────── */

  function refreshBadges() {
    if (!state.open || !dom.grid) return;
    var cards = dom.grid.querySelectorAll('.promo-products-card');
    cards.forEach(function (card) {
      var numericId = card.dataset.numericId;
      if (!numericId) return;
      var qty = inCartQtyForProduct(numericId);
      var badge = card.querySelector('[data-card-incart]');
      var label = card.querySelector('[data-card-add-label]');
      if (badge) {
        if (qty > 0) {
          badge.textContent = 'In cart: ' + qty;
          badge.classList.remove('hidden');
          card.classList.add('promo-products-card--in-cart');
          if (label && !card.querySelector('[data-card-add]').disabled) {
            label.textContent = 'Add another';
          }
        } else {
          badge.classList.add('hidden');
          card.classList.remove('promo-products-card--in-cart');
          if (label && !card.querySelector('[data-card-add]').disabled) {
            label.textContent = 'Add to cart';
          }
        }
      }
    });
  }

  /* ──────────────────────────────────────────────────────
     Public API
  ────────────────────────────────────────────────────── */

  function open(opts) {
    opts = opts || {};
    if (!cacheDom()) {
      console.warn('[' + SOURCE_LABEL + '] modal DOM not present — snippet not rendered?');
      return;
    }
    state.products = Array.isArray(opts.products) ? opts.products.slice() : [];
    state.currentNumericId = opts.currentNumericId || null;
    state.mode = opts.mode || 'min-qty';

    if (dom.eyebrow) dom.eyebrow.textContent = opts.eyebrow || '';
    if (dom.title) dom.title.textContent = opts.title || '';
    if (dom.sub) dom.sub.textContent = opts.sub || '';
    if (dom.footnote) dom.footnote.textContent = opts.footnote || '';

    bindGridDelegation();

    // Render the grid SYNCHRONOUSLY with the current opts.products
    // before the dialog opens. Otherwise the previous open's cards
    // flash on screen for a frame or two while we wait for fetchCart()
    // to resolve. In-cart badges use whatever state.cartLines we have
    // cached from a prior fetch; the post-open fetchCart() refreshes
    // them so they reflect the latest cart state.
    renderGrid();

    if (typeof dom.dialog.showModal === 'function') {
      try { dom.dialog.showModal(); } catch (e) { /* already open */ }
    } else {
      // Older browsers without <dialog> — degrade to a basic [open] shim.
      dom.dialog.setAttribute('open', '');
    }
    state.open = true;
    document.body.classList.add('promo-products-modal-open');

    // Force a fresh /cart.js fetch on every open so badges reflect any
    // cart change made between modal sessions. Otherwise a Quick Add
    // → close → open could show stale "In cart: N" copy until the
    // background tick.
    state.inflightCartFetch = null;
    fetchCart().then(refreshBadges);
  }

  function close() {
    if (!cacheDom()) return;
    if (typeof dom.dialog.close === 'function') {
      try { dom.dialog.close(); } catch (e) { /* not open */ }
    } else {
      dom.dialog.removeAttribute('open');
    }
    state.open = false;
    document.body.classList.remove('promo-products-modal-open');
  }

  function isOpen() {
    return state.open;
  }

  /* ──────────────────────────────────────────────────────
     Bootstrap
  ────────────────────────────────────────────────────── */

  function init() {
    if (!cacheDom()) return;
    if (dom.closeBtn) {
      dom.closeBtn.addEventListener('click', function () { close(); });
    }
    // Native <dialog> dispatches 'close' on Esc / closeBtn / .close().
    dom.dialog.addEventListener('close', function () {
      state.open = false;
      document.body.classList.remove('promo-products-modal-open');
    });
    // Backdrop click — native dialog doesn't auto-close on backdrop;
    // we dispatch via target check.
    dom.dialog.addEventListener('click', function (e) {
      if (e.target === dom.dialog) close();
    });
    document.addEventListener('cart:updated', function () {
      if (!state.open) return;
      // Defer to next tick to let the originating module finish.
      Promise.resolve().then(function () {
        // Force re-fetch (clear inflight) so the badge reflects the
        // latest cart state — caches can leave us behind.
        state.inflightCartFetch = null;
        fetchCart().then(refreshBadges);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.PromoProductsModal = {
    open: open,
    close: close,
    isOpen: isOpen,
  };
})();
